import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Lock, Rocket, Heart } from "lucide-react";

const principles = [
  {
    icon: <Target className="h-6 w-6 text-tasktide-teal" />,
    title: 'One Tool. One Job.',
    desc: 'We do not build platforms with 47 features nobody uses. Each product does one thing, completely. ColdMail AI writes cold emails. That is it. It does them better than any general-purpose tool.'
  },
  {
    icon: <Lock className="h-6 w-6 text-tasktide-purple" />,
    title: 'Local by Default',
    desc: 'Your data never leaves your machine. No cloud inference subscriptions. No API rate limits. No privacy policy to read. Download the source, run it locally, own everything.'
  },
  {
    icon: <Rocket className="h-6 w-6 text-tasktide-orange" />,
    title: 'Ship Every Hour',
    desc: 'An autonomous pipeline picks the next product brief, writes the code, runs the tests, and commits to GitHub. No human bottleneck. No weeks of planning. A working product, every hour.'
  },
  {
    icon: <Heart className="h-6 w-6 text-rose-400" />,
    title: 'Real Code, Not Demos',
    desc: 'Every product has a working backend, a real database, actual tests, and a UI you can click. No mock data. No "coming soon" buttons. If it is listed, it runs. If it does not run yet, we say so.'
  }
];

const AboutMission = () => {
  return (
    <div className="min-h-screen flex flex-col bg-dark-500">
      <Navbar />
      <main className="flex-grow pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          <div className="mb-4">
            <Link to="/about" className="text-sm text-gray-500 hover:text-tasktide-teal transition-colors">
              ← Back to About
            </Link>
          </div>

          <div className="mb-16">
            <span className="text-sm text-tasktide-teal font-medium">Our Mission</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              Build Tools That Actually Work
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl">
              The AI industry is full of promises and prototypes. We build the opposite: 
              small, focused tools that solve one problem completely. You download them, 
              run them locally, and they work. No pitch decks. No waitlists. Just code.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {principles.map((p) => (
              <div key={p.title} className="glass-card p-6 rounded-xl border border-white/5">
                <div className="mb-4">{p.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="glass-card p-8 rounded-xl border border-white/5 mb-16">
            <h2 className="text-2xl font-bold text-white mb-4">Why This Exists</h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              I started TaskTide because I was tired of two things: overpriced AI platforms that 
              lock you into subscriptions, and "AI wrappers" that are just a frontend for an API call 
              with no real functionality behind them.
            </p>
            <p className="text-gray-400 leading-relaxed mb-4">
              The products here are built by an autonomous pipeline that writes real code: 
              FastAPI backends, SQLModel databases, pytest suites, and React frontends. 
              Each one takes about an hour from brief to commit. The pipeline is not magic — 
              it is just relentless consistency applied to a single stack.
            </p>
            <p className="text-gray-400 leading-relaxed">
              The goal is 1,000 products. Not 1,000 features in one bloated platform. 
              One thousand single-purpose tools, each solving one job better than anything else 
              on the market. You pick the ones you need, run them locally, and never think about 
              them again.
            </p>
          </div>

          <div className="flex flex-wrap gap-4">
            <Button asChild className="bg-gradient-to-r from-tasktide-teal to-tasktide-blue text-white">
              <Link to="/projects">
                See the Catalog <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-white/10 text-white hover:bg-dark-300/50">
              <Link to="/contact">Get in Touch →</Link>
            </Button>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutMission;